<script setup>
import {JUNIOR_TICK_HELP, JUNIOR_TICK_LABEL} from '~/composables/useJuniorMembers';
import { computed, ref, watch } from 'vue';
import MemberShareLinkView from '~/components/admin/MemberShareLinkView.vue';

const props = defineProps({
  formType: String,
  errors: Object,
  readOnly: false,
  action: String,
});

const item = defineModel();
const { t } = useI18n();

const showSuccessView = computed(() => {
  return props.action === 'create' && item.value.inviteShortUrl && item.value.id;
});

// Email handling — mirrors the Flutter player form:
//  • Email is only relevant when the member is NOT admin-managed.
//  • Once the member has a user account, their details are read-only here.
//  • A new email for an account-less member sends an invite link.
const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';
const hasAccount = computed(() => {
  const uid = item.value?.userId;
  return !!uid && uid !== EMPTY_GUID;
});
const currentEmail = computed(() => (item.value?.email || '').trim());
const initialEmail = ref('');
watch(() => item.value?.id, () => {
  initialEmail.value = (item.value?.email || '').trim();
}, { immediate: true });
const willInvite = computed(() =>
    !hasAccount.value &&
    currentEmail.value !== '' &&
    currentEmail.value !== initialEmail.value);
</script>

<template>
  <!-- Success view after member creation -->
  <member-share-link-view
      v-if="showSuccessView"
      :invite-url="item.inviteShortUrl"
      :member-name="item.firstName"
  />

  <!-- Add / edit member form -->
  <v-row v-else>
    <!-- Read-only notice when the member owns their account -->
    <v-col v-if="hasAccount" cols="12">
      <v-alert type="info" density="compact" icon="mdi-account-lock-outline" class="mb-2">
        This member has their own account and manages their own profile. You can change their role
        or remove them from the members list menu.
      </v-alert>
    </v-col>

    <v-col cols="12" sm="6">
      <div class="tl-label mb-2">First name *</div>
      <v-text-field
          v-model="item.firstName"
          :disabled="hasAccount"
          :error="errors.firstName != null"
          :error-messages="errors.firstName"
      />
    </v-col>
    <v-col cols="12" sm="6">
      <div class="tl-label mb-2">Last name *</div>
      <v-text-field
          v-model="item.lastName"
          :disabled="hasAccount"
          :error="errors.lastName != null"
          :error-messages="errors.lastName"
      />
    </v-col>

    <v-col cols="12">
      <div class="tl-label mb-2">Telephone (optional)</div>
      <v-text-field
          v-model="item.telephone"
          autocomplete="off"
          prepend-inner-icon="mdi-phone"
          :disabled="hasAccount"
          :error="errors.telephone != null"
          :error-messages="errors.telephone"
      />
    </v-col>

    <!-- Account: admin-managed toggle in a bordered box (mockup .opt-row) -->
    <v-col cols="12">
      <div class="tl-label mb-2">Account</div>
      <div class="d-flex align-center ga-3 pa-3 border rounded-lg">
        <v-switch
            v-model="item.noUser"
            :true-value="true"
            :false-value="false"
            :disabled="hasAccount"
            hide-details
            class="flex-grow-0 flex-shrink-0"
        />
        <span class="text-body-2 text-medium-emphasis">{{ t('member_form_no_email_option') }}</span>
      </div>
    </v-col>

    <!-- Under 18. Shown for every member, not only admin-managed ones: 14,045
         of 14,061 accounts have no date of birth, so for nearly everybody this
         tick is the only signal there is. The help text states the precedence
         rather than hiding the control, because an admin who ticks somebody and
         sees nothing change deserves to know why. -->
    <v-col cols="12">
      <div class="tl-label mb-2">Age</div>
      <!-- One box: the statement and its help are the tick's label, so the
           whole row ticks (it was a bordered row round a separately bordered
           tick, with a label that did nothing when clicked). -->
      <v-checkbox
          v-model="item.isJunior"
          :true-value="true"
          :false-value="false"
          hide-details
          density="compact">
        <template #label>
          <span class="d-flex flex-column py-1">
            <span class="text-body-2 font-weight-bold">{{ JUNIOR_TICK_LABEL }}</span>
            <span class="text-caption text-medium-emphasis font-weight-regular">{{ JUNIOR_TICK_HELP }}</span>
          </span>
        </template>
      </v-checkbox>
    </v-col>

    <!-- Warning when admin-managed (no login) -->
    <v-col v-if="item.noUser" cols="12">
      <v-alert type="warning" density="compact">
        {{ t('member_form_no_email_option_warning') }}
      </v-alert>
    </v-col>

    <!-- Email — only when NOT admin-managed (they can sign in) -->
    <v-col v-if="!item.noUser" cols="12">
      <div class="tl-label mb-2">Email (optional)</div>
      <v-text-field
          v-model="item.email"
          type="email"
          autocomplete="off"
          prepend-inner-icon="mdi-email-outline"
          :disabled="hasAccount"
          :hint="hasAccount ? 'This member manages their own email from their account.' : undefined"
          :persistent-hint="hasAccount"
          :error="errors.email != null"
          :error-messages="errors.email"
      />
      <div v-if="willInvite" class="d-flex align-center text-success text-caption font-weight-medium mt-2">
        <v-icon size="14" class="mr-1">mdi-email-arrow-right-outline</v-icon>
        An invite link will be sent to {{ currentEmail }}
      </div>
    </v-col>

    <!-- Heads-up: a name-only member gets a shareable claim link -->
    <v-col v-if="action === 'create' && !willInvite" cols="12">
      <div class="d-flex align-start text-medium-emphasis text-caption">
        <v-icon size="16" class="mr-2">mdi-link-variant</v-icon>
        <span>After adding, you'll get a link to share with this member so they can access their account.</span>
      </div>
    </v-col>
  </v-row>
</template>
